import type { CatalogResp, SubscriptionView } from "../api/accountApi";

const PREVIEW_KEY = "cp-account-preview";
const PREVIEW_PLANS_KEY = "cp-account-preview-plans";

/** Local fixture session. Never active in a production build. */
export function accountPreviewEnabled(): boolean {
  if (!import.meta.env.DEV || typeof window === "undefined") return false;
  const path = window.location.pathname;
  if (path === "/vorschau" || path.startsWith("/vorschau/")) {
    sessionStorage.setItem(PREVIEW_KEY, "1");
    return true;
  }
  return sessionStorage.getItem(PREVIEW_KEY) === "1";
}

export function clearAccountPreview(): void {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(PREVIEW_KEY);
  sessionStorage.removeItem(PREVIEW_PLANS_KEY);
}

/** Preview without an active membership — used to review the plan cards. */
export function accountPreviewPlans(): boolean {
  if (!accountPreviewEnabled() || typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get("plans") === "1") {
    sessionStorage.setItem(PREVIEW_PLANS_KEY, "1");
    return true;
  }
  if (params.get("plans") === "0") {
    sessionStorage.removeItem(PREVIEW_PLANS_KEY);
    return false;
  }
  return sessionStorage.getItem(PREVIEW_PLANS_KEY) === "1";
}

const periodEnd = Math.floor(Date.UTC(2026, 10, 24) / 1000);

export const previewSubscription: SubscriptionView = {
  product_code: "customer",
  status: "active",
  cancel_at_period_end: false,
  current_period_end: periodEnd,
  entitled: true,
  stripe_configured: true,
  products: [
    {
      code: "customer",
      label: "Capital Pilot Basis",
      app: "client-desktop",
      price_configured: true,
      limits: { properties: 5, bank_accounts: 3 },
    },
    {
      code: "customer_plus",
      label: "Capital Pilot Plus",
      app: "client-desktop",
      price_configured: true,
      limits: { properties: 25, bank_accounts: 10 },
    },
    {
      code: "customer_premium",
      label: "Capital Pilot Premium",
      app: "client-desktop",
      price_configured: true,
      limits: { properties: null, bank_accounts: null },
    },
    {
      code: "advisor",
      label: "Capital Pilot Advisor",
      app: "advisor-desktop",
      price_configured: true,
    },
    {
      code: "ai_lotse",
      label: "KI-Lotse",
      app: "client-desktop",
      price_configured: true,
      price_copy: "10 € / Monat",
    },
  ],
  ai_lotse: {
    product_code: "ai_lotse",
    status: null,
    entitled: false,
    cancel_at_period_end: false,
    current_period_end: null,
    usage: null,
    consent_version: null,
    consent_granted: false,
  },
};

export const previewOpenSubscription: SubscriptionView = {
  ...previewSubscription,
  product_code: null,
  status: null,
  cancel_at_period_end: false,
  current_period_end: null,
  entitled: false,
};

export const previewCatalog: CatalogResp = {
  apps: [
    {
      app: "client-desktop",
      product_code: "customer",
      label: "Capital Pilot",
      entitled: true,
      version: "0.1.25",
      published_at: "2026-10-20T08:00:00Z",
      notes: [
        "Nur Beispieldaten dieser lokalen Vorschau",
        "Ein Installer für das erkannte System",
        "Mitgliedschaft und Passwort als Folgezeilen",
        "Hinweis zu SmartScreen und Gatekeeper",
      ].join("\n"),
      assets: [
        {
          platform: "windows-x86_64",
          label: "Windows (x64)",
          filename: "Capital Pilot_0.1.25_x64-setup.exe",
          version: "0.1.25",
          download_path: "/downloads/preview/windows",
          size_bytes: 86_400_000,
        },
        {
          platform: "darwin-universal",
          label: "macOS (Universal)",
          filename: "Capital Pilot_0.1.25_universal.dmg",
          version: "0.1.25",
          download_path: "/downloads/preview/macos",
          size_bytes: 94_200_000,
        },
      ],
    },
    {
      app: "advisor-desktop",
      product_code: "advisor",
      label: "Capital Pilot Advisor",
      entitled: false,
      version: "0.1.25",
      assets: [],
    },
  ],
};

export const PREVIEW_ACCOUNT = {
  sub: "sub-1042",
  email: "vorschau@capitalpilot.local",
  createdAt: Math.floor(Date.UTC(2026, 2, 4) / 1000),
};
